import { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Popover,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Business,
  Language,
  AccountTree,
  // Check,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PublicIcon from "@mui/icons-material/Public";
import { selectUserMenu } from "../../../features/userSlice";
import {
  selectSelectedWorkspace,
  selectAvailableWorkspaces,
  setSelectedWorkspace,
  type Workspace,
} from "../../../features/workspaceSlice";
import { useDeviceDetection } from "../../../customHooks/useDeviceDetection";
import { getWorkspaceImage } from "./workspaceImages";
import { colors, spacing, typography } from "../../../assets/styles/tokens";
import { resetOnWorkspaceChange } from "../../../constants/resetOnWorkspaceChange";

interface WorkspaceDropdownProps {
  isDrawerOpen: boolean;
}

/**
 * Returns true if the user has `view_organizational_summary`
 * anywhere inside products.assets[*].overview[*].
 */
const hasOrgSummaryPermission = (products: any): boolean => {
  if (!products?.assets || !Array.isArray(products.assets)) return false;
  for (const assetObj of products.assets) {
    const overview = assetObj?.overview;
    if (!Array.isArray(overview)) continue;
    for (const permObj of overview) {
      if (permObj && typeof permObj === "object" && "view_organizational_summary" in permObj) {
        return true;
      }
    }
  }
  return false;
};

const WorkspaceDropdown = ({ isDrawerOpen }: WorkspaceDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectedWorkspace = useSelector(selectSelectedWorkspace);
  const availableWorkspaces = useSelector(selectAvailableWorkspaces);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const menuPermissions = useSelector(selectUserMenu);
  const canSeeAllWebsites = hasOrgSummaryPermission(menuPermissions);

  // Detect if "All Websites" is currently active (workspace param contains all site IDs)
  const currentWorkspaceParam = searchParams.get("workspace") ?? "";
  const allWorkspaceIds = availableWorkspaces.map((w: Workspace) => w.id).join(",");
  const isAllWebsitesSelected =
    canSeeAllWebsites &&
    availableWorkspaces.length > 1 &&
    currentWorkspaceParam === allWorkspaceIds;
  const { deviceType } = useDeviceDetection();

  // Environment detection
  const environment = import.meta.env.VITE_ENV;
  const isProduction = environment === "production";
  const isStaging = environment === "staging" || environment === "development";
  const isDesktop = deviceType === "desktop";

  // Hide workspace dropdown in production completely
  // Hide workspace dropdown in staging for desktop only
  const shouldHideWorkspace =
    (isProduction && isDesktop) || (isStaging && isDesktop);

  // get Workspace Image
  const avatarImage = getWorkspaceImage(selectedWorkspace?.name);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWorkspaceSelect = (workspace: Workspace) => {
    dispatch(setSelectedWorkspace(workspace));
    //for clearing states and updating URL
    setSearchParams((prev) => {
      let updated = resetOnWorkspaceChange({
        dispatch,
        searchParams: prev,
      });
      updated.set("workspace", workspace.id);
      return updated;
    });
    handleClose();
  };

  const handleAllWebsitesSelect = () => {
    // Set workspace param to all comma-joined IDs (no single selectedWorkspace change)
    setSearchParams((prev) => {
      let updated = resetOnWorkspaceChange({
        dispatch,
        searchParams: prev,
      });
      updated.set("workspace", allWorkspaceIds);
      return updated;
    });
    handleClose();
  };

  const open = Boolean(anchorEl);

  const getWorkspaceIcon = (type: string) => {
    switch (type) {
      case "website":
        return <Language sx={{ fontSize: typography.Size.Text.sm.$value }} />;
      case "dimension":
        return (
          <AccountTree sx={{ fontSize: typography.Size.Text.sm.$value }} />
        );
      case "org":
        return <Business sx={{ fontSize: typography.Size.Text.sm.$value }} />;
      default:
        return <Language sx={{ fontSize: typography.Size.Text.sm.$value }} />;
    }
  };

  const cleanUrl = (url: string) => {
    if (!url) return "";
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  };

  // Don't render anything if workspace should be hidden
  if (shouldHideWorkspace) {
    return null;
  }

  return (
    <>
      {/* Workspace Selector in Drawer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: spacing.Spacing.lg.$value,
          borderRadius: spacing.Radius.md.$value,
          mx: 2,
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          backgroundColor: colors.GrayLightMode[100].$value,
          "&:hover": {
            backgroundColor: colors.GrayLightMode[100].$value,
          },
        }}
        onClick={handleClick}
        data-tracker="630"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flex: 1,
            minWidth: spacing.Spacing.none.$value,
          }}
        >
          {/* <Avatar
            sx={{
              bgcolor: colors.Brand[700].$value,
              width: 32,
              height: 32,
              fontSize: typography.Size.Text.sm.$value,
              fontWeight: typography.Font_weight.Medium.$value,
            }}
          >
            {selectedWorkspace?.icon || selectedWorkspace?.name.charAt(0)}
          </Avatar> */}
          {isAllWebsitesSelected ? (
            <Avatar
              sx={{
                bgcolor: colors.Brand[50].$value,
                width: spacing.Spacing["5xl"].$value,
                height: spacing.Spacing["5xl"].$value,
                borderRadius: spacing.Radius.xs.$value,
              }}
            >
              <PublicIcon
                sx={{
                  color: colors.Brand[700].$value,
                  fontSize: typography.Size.Text.lg.$value,
                }}
              />
            </Avatar>
          ) : (
            <Avatar
              src={avatarImage || undefined}
              sx={{
                bgcolor: avatarImage ? "transparent" : colors.Brand[700].$value,
                width: spacing.Spacing["5xl"].$value,
                height: spacing.Spacing["5xl"].$value,
                borderRadius: spacing.Radius.xs.$value,
                fontSize: typography.Size.Text.sm.$value,
                fontWeight: typography.Font_weight.Medium.$value,
              }}
            >
              {!avatarImage &&
                (selectedWorkspace?.icon || selectedWorkspace?.name.charAt(0))}
            </Avatar>
          )}
          {isDrawerOpen && (
            <Box sx={{ flex: 1, minWidth: spacing.Spacing.none.$value }}>
              <Typography
                sx={{
                  fontWeight: typography.Font_weight.Medium.$value,
                  fontSize: typography.Size.Text.sm.$value,
                  lineHeight: typography.Line_height.text.sm.$value,
                  color: colors.GrayLightMode[700].$value,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mb: spacing.Spacing["xxs"].$value,
                }}
              >
                {isAllWebsitesSelected ? "All Websites" : (selectedWorkspace?.name || "Select Workspace")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* <Box
                  sx={{
                    color: "rgba(0, 0, 0, 0.6)",
                    display: "flex",
                    alignItems: "center",
                    mt: "2px",
                  }}
                >
                  {getWorkspaceIcon(
                    selectedWorkspace?.metadata?.type || "Touch to change workspace",
                  )}
                </Box> */}
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    lineHeight: typography.Line_height.text.sm.$value,
                    color: colors.GrayLightMode[700].$value,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isAllWebsitesSelected
                    ? `${availableWorkspaces.length} websites selected`
                    : (selectedWorkspace?.metadata?.type || "Touch to change workspace")}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {isDrawerOpen && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {open ? (
              <ExpandLess
                sx={{
                  color: colors.GrayLightMode[700].$value,
                  fontSize: typography.Size.Display.xs.$value,
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  color: colors.GrayLightMode[700].$value,
                  fontSize: typography.Size.Display.xs.$value,
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Workspace Selection Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 305,
            maxHeight: spacing.Width.sm.$value,
            mt: 1,
            boxShadow: "0px 5px 15px" + colors.GrayLightMode[300].$value,
            borderRadius: spacing.Radius.xs.$value,
            border: "1px solid" + colors.GrayLightMode[200].$value,
          },
        }}
      >
        {/* Header */}
        {/* <Box sx={{ p: "16px 20px" }}>
          <Typography
            sx={{
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: "16px",
              lineHeight: "24px",
              color: colors.GrayLightMode[600].$value,
            }}
          >
            Switch Workspace
          </Typography>
        </Box>

        <Divider /> */}

        {/* Workspace List */}
        <List
          sx={{
            py: spacing.Spacing.none.$value,
            maxHeight: spacing.Width.sm.$value,
            overflow: "auto",
          }}
        >
          {/* All Websites option — only shown when user has view_organizational_summary and there are 2+ sites */}
          {canSeeAllWebsites && availableWorkspaces.length > 1 && (
            <ListItem disablePadding>
              <ListItemButton
                selected={isAllWebsitesSelected}
                onClick={handleAllWebsitesSelect}
                sx={{
                  px: spacing.Spacing["2xl"].$value,
                  py: spacing.Spacing.lg.$value,
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.Spacing.lg.$value,
                  "&.Mui-selected": {
                    backgroundColor: colors.Brand[50].$value,
                    "&:hover": {
                      backgroundColor: colors.Brand[100].$value,
                    },
                  },
                  "&:hover": {
                    backgroundColor: colors.Brand[100].$value,
                  },
                }}
              >
                <PublicIcon
                  sx={{
                    color: isAllWebsitesSelected
                      ? colors.Brand[700].$value
                      : colors.GrayLightMode[600].$value,
                    width: spacing.Spacing["4xl"].$value,
                    height: spacing.Spacing["4xl"].$value,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: spacing.Spacing.none.$value }}>
                  <Typography
                    sx={{
                      fontWeight: typography.Font_weight.Medium.$value,
                      fontSize: typography.Size.Text.sm.$value,
                      lineHeight: typography.Line_height.text.sm.$value,
                      color: isAllWebsitesSelected
                        ? colors.Brand[700].$value
                        : colors.GrayLightMode[700].$value,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    All Websites
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: typography.Size.Text.xs.$value,
                      lineHeight: typography.Line_height.text.xs.$value,
                      color: colors.GrayLightMode[600].$value,
                    }}
                  >
                    {availableWorkspaces.length} websites selected
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          )}
          {availableWorkspaces.map((workspace: Workspace) => {
            const isSelected = workspace.id === selectedWorkspace?.id;
            const listAvatarImage = getWorkspaceImage(workspace.name);

            return (
              <ListItem key={workspace.id} disablePadding>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleWorkspaceSelect(workspace)}
                  sx={{
                    px: spacing.Spacing["2xl"].$value,
                    py: spacing.Spacing.lg.$value,
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.Spacing.lg.$value,
                    "&.Mui-selected": {
                      backgroundColor: colors.Brand[50].$value,
                      "&:hover": {
                        backgroundColor: colors.Brand[100].$value,
                      },
                    },
                    "&:hover": {
                      backgroundColor: colors.Brand[100].$value,
                    },
                  }}
                >
                  {/* <Avatar
                    sx={{
                      bgcolor: isSelected ? "#9C27B0" : "#E0E0E0",
                      color: isSelected ? "#FFFFFF" : "#616161",
                      width: 32,
                      height: 32,
                      fontSize: typography.Size.Text.sm.$value,
                      fontWeight: typography.Font_weight.Medium.$value,
                    }}
                  >
                    {workspace.icon || workspace.name.charAt(0)}
                  </Avatar> */}

                  <Avatar
                    src={listAvatarImage || undefined}
                    sx={{
                      bgcolor: listAvatarImage
                        ? "transparent"
                        : colors.Brand[700].$value,
                      color: listAvatarImage
                        ? "transparent"
                        : colors.Base.White.$value,
                      width: spacing.Spacing["4xl"].$value,
                      height: spacing.Spacing["4xl"].$value,
                      borderRadius: spacing.Radius.xs.$value,
                      fontSize: typography.Size.Text.sm.$value,
                      fontWeight: typography.Font_weight.Medium.$value,
                    }}
                  >
                    {!listAvatarImage &&
                      (workspace.icon || workspace.name.charAt(0))}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: spacing.Spacing.none.$value }}>
                    <Typography
                      sx={{
                        fontWeight: typography.Font_weight.Medium.$value,
                        fontSize: typography.Size.Text.sm.$value,
                        lineHeight: typography.Line_height.text.sm.$value,
                        color: colors.GrayLightMode[700].$value,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {workspace.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: spacing.Spacing.xxs.$value,
                      }}
                    >
                      <Box
                        sx={{
                          color: colors.GrayLightMode[600].$value,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {getWorkspaceIcon(
                          workspace.metadata?.type || "website",
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          lineHeight: typography.Line_height.text.xs.$value,
                          color: colors.GrayLightMode[600].$value,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cleanUrl(
                          workspace.metadata?.site_url ||
                            workspace.metadata?.type ||
                            "Website",
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  {isSelected && (
                    <></>
                    // <Check
                    //   sx={{
                    //     color: "#9C27B0",
                    //     fontSize: 20,
                    //   }}
                    // />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default WorkspaceDropdown;
